import { useEffect, useState } from 'react';
import { Button, FormField, Input, Modal, ModalBody, ModalFooter, ModalHeader } from '@vd/ui';
import { nextUnusedProjectColor } from '@vd/tokens';
import { ProjectColorPicker } from '../projects/ProjectColorPicker';
import { UrlPatternHelp } from '../projects/UrlPatternHelp';

export interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; color: string; urlPattern?: string }) => void;
  /**
   * Optional list of colors already in use by projects in the current
   * company. The modal pre-selects the next-unused palette color so a
   * fresh project doesn't collide with siblings. Pass [] (or omit) and
   * we fall back to palette[0].
   */
  existingColors?: readonly string[];
}

export function CreateProjectModal({ open, onClose, onCreate, existingColors }: CreateProjectModalProps) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(() => nextUnusedProjectColor(existingColors ?? []));
  const [urlPattern, setUrlPattern] = useState('');

  // Re-pick the default color whenever the modal opens so the suggestion
  // reflects the latest project list (a project added since the page
  // mounted shouldn't get re-suggested).
  useEffect(() => {
    if (open) {
      setColor(nextUnusedProjectColor(existingColors ?? []));
      setName('');
      setUrlPattern('');
    }
  }, [open, existingColors]);

  return (
    <Modal open={open} onClose={onClose} width={460}>
      <ModalHeader>New project</ModalHeader>
      {/* What the workspace tour's first project step points at (Jason, 28 Oct). */}
      <ModalBody data-tour-anchor="create-project.form">
        <FormField label="Project name">
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Checkout" />
        </FormField>
        <div style={{ height: 12 }} />
        <FormField label="Color">
          <ProjectColorPicker value={color} onChange={setColor} />
        </FormField>
        <div style={{ height: 12 }} />
        <FormField
          label="URL pattern (optional)"
          help="Captures from matching URLs auto-assign to this project."
        >
          <Input value={urlPattern} onChange={(e) => setUrlPattern(e.target.value)} placeholder="https://myapp.com" />
        </FormField>
        {/*
          * The syntax, at the moment somebody is typing it.
          *
          * This field used to suggest `https://myapp.com/*` and explain
          * nothing. A single `*` matches one path segment, so that placeholder
          * routed the top level of the site and quietly missed everything
          * deeper - and the person had no reason to doubt it until a capture
          * landed in the wrong place weeks later.
          */}
        <div style={{ marginTop: 10 }}>
          <UrlPatternHelp heading={null} />
        </div>
        {urlPattern.trim() && (
          <p style={{ margin: '6px 2px 0', fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
            Tip: after creating, add the one-line report widget in this project's Widget
            tab to capture bugs from that site without the extension.
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} data-tour-anchor="create-project.cancel">Cancel</Button>
        <Button
          variant="primary"
          data-tour-anchor="create-project.submit"
          disabled={!name.trim()}
          onClick={() => { onCreate({ name, color, urlPattern: urlPattern || undefined }); onClose(); }}
        >
          Create project
        </Button>
      </ModalFooter>
    </Modal>
  );
}
